import React, { useEffect, useState, useCallback } from "react";
import Shell from "@/components/app/Shell";
import RequestCard from "@/components/app/RequestCard";
import PmocDialog from "@/components/app/PmocDialog";
import ServiceOrderDialog from "@/components/app/ServiceOrderDialog";
import StaffEquipmentTab from "@/components/app/StaffEquipmentTab";
import TechnicianOverview from "@/components/app/TechnicianOverview";
import CreateCustomerDialog from "@/components/app/CreateCustomerDialog";
import AgendaView from "@/components/app/AgendaView";
import NewRequestDialog from "@/components/app/NewRequestDialog";
import MobileTabBar from "@/components/app/MobileTabBar";
import { SUPPORT_WHATSAPP } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Truck, CheckCircle2, ClipboardList, ClipboardCheck, FileText, Plus, Store, MapPin, Clock, HandHelping, Home, Headphones, User } from "lucide-react";

function TechActions({ request, onChanged, onPmoc, onOs }) {
  const [busy, setBusy] = useState(false);
  const update = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/requests/${request.id}/status`, { status });
      toast.success("Status atualizado!");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  const finished = request.status === "concluido" || request.status === "cancelado";
  return (
    <div className="flex gap-2 flex-wrap">
      {!finished && request.status === "atribuido" && (
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" disabled={busy}
          onClick={() => update("aceito")} data-testid={`accept-button-${request.id}`}>
          <Check className="h-4 w-4" /> Aceitar
        </Button>
      )}
      {!finished && (request.status === "aceito" || request.status === "atribuido") && (
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 gap-1.5" disabled={busy}
          onClick={() => update("a_caminho")} data-testid={`enroute-button-${request.id}`}>
          <Truck className="h-4 w-4" /> A caminho
        </Button>
      )}
      {!finished && (
        <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1.5" disabled={busy}
          onClick={() => update("concluido")} data-testid={`complete-button-${request.id}`}>
          <CheckCircle2 className="h-4 w-4" /> Concluir
        </Button>
      )}
      {request.equipment_id && (
        <Button size="sm" variant="outline" className="gap-1.5 border-[#1e7be0] text-[#1e7be0] hover:bg-blue-50"
          onClick={() => onPmoc(request)} data-testid={`pmoc-button-${request.id}`}>
          <ClipboardCheck className="h-4 w-4" /> Preencher PMOC
        </Button>
      )}
      <Button size="sm" variant="outline" className="gap-1.5"
        onClick={() => onOs(request)} data-testid={`service-order-${request.id}`}>
        <FileText className="h-4 w-4" /> Ordem de Serviço
      </Button>
    </div>
  );
}

function ClaimAction({ request, onClaimed }) {
  const [busy, setBusy] = useState(false);
  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${request.id}/claim`);
      toast.success("Chamado adicionado aos seus atendimentos!");
      onClaimed?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 w-full" disabled={busy}
      onClick={claim} data-testid={`claim-button-${request.id}`}>
      <HandHelping className="h-4 w-4" /> Pegar este chamado
    </Button>
  );
}

export default function TechnicianDashboard() {
  const { user, checkAuth } = useAuth();
  const [requests, setRequests] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pmocReq, setPmocReq] = useState(null);
  const [osReq, setOsReq] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [tab, setTab] = useState("inicio");

  const approved = user?.credential_status === "aprovado" || !user?.credential_status;
  const pending = user?.credential_status === "pendente";
  const rejected = user?.credential_status === "recusado";
  const isAvailable = user?.is_available !== false;

  const load = useCallback(async () => {
    const { data } = await api.get("/requests");
    setRequests(data);
    setLoading(false);
  }, []);

  const loadAvailable = useCallback(async () => {
    try {
      const { data } = await api.get("/technician/available");
      setAvailable(data);
    } catch { setAvailable([]); }
  }, []);

  useEffect(() => { load(); loadAvailable(); }, [load, loadAvailable]);

  const afterClaim = () => { load(); loadAvailable(); };

  const toggleAvailability = async () => {
    try {
      await api.patch("/me/availability", { is_available: !isAvailable });
      await checkAuth();
      loadAvailable();
      toast.success(!isAvailable ? "Você está Disponível — recebendo chamados no balcão." : "Você está Ocupado — novos chamados pausados.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao atualizar status.");
    }
  };

  const setEnroute = async (request) => {
    try {
      await api.patch(`/requests/${request.id}/status`, { status: "a_caminho" });
      toast.success("Cliente avisado: você está a caminho!");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openSupport = () => {
    const p = SUPPORT_WHATSAPP.replace(/\D/g, "");
    window.open(`https://wa.me/${p}?text=${encodeURIComponent("Olá, sou técnico Clima Branca e preciso de suporte.")}`, "_blank");
  };

  const ativos = requests.filter((r) => !["concluido", "cancelado"].includes(r.status));

  useEffect(() => { if (!approved && tab === "inicio") setTab("atendimentos"); }, [approved, tab]);
  const finalizados = requests.filter((r) => ["concluido", "cancelado"].includes(r.status));

  return (
    <Shell
      title="Meus atendimentos"
      subtitle="Chamados atribuídos, pegue novos no balcão e controle seus atendimentos"
      actions={approved ? (
        <div className="flex gap-2">
          <CreateCustomerDialog />
          <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={() => setNewOpen(true)}
            data-testid="tech-new-request-button">
            <Plus className="h-4 w-4" /> Novo atendimento
          </Button>
        </div>
      ) : null}
    >
      {pending && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="credential-pending-banner">
          <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">Credenciamento em análise</p>
            <p className="text-sm text-amber-700">Assim que o gestor aprovar e definir sua área, você poderá pegar e abrir chamados.</p>
          </div>
        </div>
      )}
      {rejected && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="credential-rejected-banner">
          <ClipboardList className="h-5 w-5 text-red-500 mt-0.5" />
          <div>
            <p className="font-semibold text-red-700">Credenciamento não aprovado</p>
            <p className="text-sm text-red-600">Entre em contato com o gestor para mais informações.</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            {approved && <TabsTrigger value="inicio" data-testid="tech-tab-inicio">Início</TabsTrigger>}
            <TabsTrigger value="atendimentos" data-testid="tech-tab-atendimentos">Atendimentos</TabsTrigger>
            {approved && <TabsTrigger value="balcao" data-testid="tech-tab-balcao">Balcão ({available.length})</TabsTrigger>}
            {approved && <TabsTrigger value="equipamentos" data-testid="tech-tab-equipamentos">Equipamentos</TabsTrigger>}
            <TabsTrigger value="agenda" data-testid="tech-tab-agenda">Agenda</TabsTrigger>
          </TabsList>

          {approved && (
            <TabsContent value="inicio">
              <TechnicianOverview requests={requests} available={available} userName={user?.name}
                onStart={setOsReq} onNew={() => setNewOpen(true)} onNav={setTab}
                isAvailable={isAvailable} onToggleAvailability={toggleAvailability}
                onEnroute={setEnroute} onSupport={openSupport} />
            </TabsContent>
          )}

          <TabsContent value="atendimentos">
            {requests.length === 0 ? (
              <div className="text-center py-16" data-testid="empty-state">
                <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nenhum atendimento ainda. {approved && "Pegue um chamado no Balcão ou abra um novo."}</p>
              </div>
            ) : (
              <div className="space-y-8">
                <section>
                  <h2 className="text-sm font-semibold text-slate-500 mb-3">Em andamento ({ativos.length})</h2>
                  {ativos.length === 0 ? (
                    <p className="text-slate-400 text-sm">Nenhum atendimento em andamento.</p>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2" data-testid="active-requests-list">
                      {ativos.map((r) => (
                        <RequestCard key={r.id} request={r} showCustomer
                          actions={<TechActions request={r} onChanged={load} onPmoc={setPmocReq} onOs={setOsReq} />} />
                      ))}
                    </div>
                  )}
                </section>
                {finalizados.length > 0 && (
                  <section>
                    <h2 className="text-sm font-semibold text-slate-500 mb-3">Finalizados ({finalizados.length})</h2>
                    <div className="grid gap-4 sm:grid-cols-2" data-testid="finished-requests-list">
                      {finalizados.map((r) => (
                        <RequestCard key={r.id} request={r} showCustomer
                          actions={r.status === "concluido" ? (
                            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setOsReq(r)}
                              data-testid={`service-order-done-${r.id}`}>
                              <FileText className="h-4 w-4" /> Ordem de Serviço / Equipamentos
                            </Button>
                          ) : null} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </TabsContent>

          {approved && (
            <TabsContent value="balcao">
              <div className="mb-3 flex items-center gap-2 text-sm text-slate-500">
                <Store className="h-4 w-4 text-emerald-600" />
                Chamados abertos na sua área de atuação. Pegue os que quiser atender.
              </div>
              {available.length === 0 ? (
                <div className="text-center py-16" data-testid="balcao-empty">
                  <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Nenhum chamado disponível na sua área no momento.</p>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2" data-testid="available-requests-list">
                  {available.map((r) => (
                    <RequestCard key={r.id} request={r} showCustomer
                      actions={<ClaimAction request={r} onClaimed={afterClaim} />} />
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {approved && (
            <TabsContent value="equipamentos">
              <StaffEquipmentTab />
            </TabsContent>
          )}

          <TabsContent value="agenda">
            <AgendaView requests={requests}
              onReschedule={async (id, date, time) => {
                try { await api.patch(`/requests/${id}/schedule`, { scheduled_date: date, scheduled_time: time }); toast.success("Chamado reagendado."); load(); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha ao reagendar."); }
              }} />
          </TabsContent>
        </Tabs>
      )}

      <NewRequestDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} managerMode />
      <PmocDialog open={!!pmocReq} onOpenChange={(o) => !o && setPmocReq(null)}
        equipmentId={pmocReq?.equipment_id} requestId={pmocReq?.id} onSaved={load} />
      <ServiceOrderDialog request={osReq} open={!!osReq} onOpenChange={(o) => !o && setOsReq(null)}
        onSaved={load} />

      {approved && (
        <MobileTabBar items={[
          { key: "inicio", label: "Início", icon: Home, onClick: () => setTab("inicio"), active: tab === "inicio" },
          { key: "atendimentos", label: "Chamados", icon: ClipboardList, onClick: () => setTab("atendimentos"), active: tab === "atendimentos" },
          { type: "fab", label: "Novo", icon: Plus, onClick: () => setNewOpen(true) },
          { key: "suporte", label: "Suporte", icon: Headphones, onClick: openSupport },
          { type: "menu", key: "perfil", label: "Perfil", icon: User, links: [
            { key: "balcao", label: `Balcão (${available.length})`, icon: Store, onClick: () => setTab("balcao") },
            { key: "agenda", label: "Agenda", icon: Clock, onClick: () => setTab("agenda") },
            { key: "equipamentos", label: "Equipamentos", icon: ClipboardCheck, onClick: () => setTab("equipamentos") },
          ] },
        ]} />
      )}
    </Shell>
  );
}
